import {useEffect} from 'react';

// The focusTrap behavior is used prevent focus from leaving a particular element.
// This is useful for implementing modal dialogs.
// Only the content within the dialog should be interactive,
// even though the UI underneath may still be visible.

function useFocusTrap(elementRef: React.RefObject<any>): void {
  function handleFocus(keyboardEvent) {
    const TAB_KEY_CODE = 9;

    const focusableElements = elementRef.current.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select',
    );
    const firstFocusableElement = focusableElements[0] as HTMLElement;
    const lastFocusableElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    const isTabPressed =
      keyboardEvent.key === 'Tab' || keyboardEvent.keyCode === TAB_KEY_CODE;

    if (!isTabPressed) {
      return;
    }

    if (keyboardEvent.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        // shift + tab, returns to previous focusable element
        lastFocusableElement.focus();
        keyboardEvent.preventDefault();
      }
    } else if (document.activeElement === lastFocusableElement) {
      // shift + tab, advances to next focusable element
      firstFocusableElement.focus();
      keyboardEvent.preventDefault();
    }
  }

  useEffect(() => {
    elementRef.current?.addEventListener('keydown', handleFocus);

    return () => {
      elementRef.current?.removeEventListener('keydown', handleFocus);
    };
  }, []);
}

export default useFocusTrap;
